import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Star } from 'lucide-react'
import { CsatTrendCard } from './CsatTrendCard'
import { formatPercent } from './formatters'
import type { CsatPanel } from '@/types/analytics'

interface CsatReportProps {
  data: CsatPanel
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex shrink-0">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={i < rating ? 'size-3.5 fill-warning text-warning' : 'size-3.5 text-muted-foreground/40'}
        />
      ))}
    </span>
  )
}

function StatTile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card className="p-4 flex flex-col gap-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{hint}</div>
    </Card>
  )
}

export function CsatReport({ data }: CsatReportProps) {
  const distributionTotal = data.distribution.reduce((sum, d) => sum + d.count, 0)

  return (
    <div className="space-y-6">
      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Avg rating" value={data.average != null ? `${data.average.toFixed(1)} ★` : '—'} hint="this range" />
        <StatTile label="Responses" value={String(data.response_count)} hint={`${formatPercent(data.response_rate)} response rate`} />
        <StatTile label="Positive" value={formatPercent(data.positive_rate)} hint="rated 4–5★" />
        <StatTile label="Requests sent" value={String(data.request_count)} hint="this range" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CsatTrendCard data={data} />

        {/* Rating distribution */}
        <Card className="p-4">
          <CardHeader className="px-0 pb-3">
            <CardTitle className="text-base">Rating distribution</CardTitle>
            <CardDescription>How the {distributionTotal} responses break down</CardDescription>
          </CardHeader>
          <CardContent className="px-0 space-y-2">
            {distributionTotal === 0 ? (
              <p className="text-sm text-muted-foreground">No ratings in this range yet.</p>
            ) : (
              [...data.distribution].reverse().map((d) => (
                <div key={d.rating} className="flex items-center gap-3 text-sm">
                  <span className="flex items-center gap-1 w-12 tabular-nums">
                    {d.rating} <Star className="size-3 fill-warning text-warning" />
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-warning" style={{ width: `${(d.count / distributionTotal) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right tabular-nums text-muted-foreground">{d.count}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Per-agent CSAT */}
        <Card className="p-4">
          <CardHeader className="px-0 pb-3">
            <CardTitle className="text-base">CSAT by agent</CardTitle>
            <CardDescription>Average rating + response count</CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            {data.by_agent.length === 0 ? (
              <p className="text-sm text-muted-foreground">No agent-attributed ratings yet.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.by_agent.map((a) => (
                    <tr key={a.name} className="border-b last:border-b-0">
                      <td className="py-2 font-medium">{a.name}</td>
                      <td className="py-2 text-right tabular-nums">
                        {a.average.toFixed(1)} ★ <span className="text-muted-foreground">({a.count})</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Recent comments */}
        <Card className="p-4">
          <CardHeader className="px-0 pb-3">
            <CardTitle className="text-base">Recent comments</CardTitle>
            <CardDescription>Latest written feedback</CardDescription>
          </CardHeader>
          <CardContent className="px-0 space-y-3">
            {data.recent_comments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No comments in this range yet.</p>
            ) : (
              data.recent_comments.map((c, i) => (
                <div key={i} className="border-b last:border-b-0 pb-3 last:pb-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{c.name}</span>
                    <Stars rating={c.rating} />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{c.comment}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
